import { useEffect, useRef, useState } from "react";

/**
 * Cámara en vivo dentro de Donna (computadora, tableta o celular).
 * El navegador pide permiso solo la primera vez; al aceptarlo queda recordado para este sitio.
 * Donna además recuerda la última cámara usada (frontal, trasera o la webcam elegida).
 */
const CLAVE = "donna.camara";
const leer = () => { try { return localStorage.getItem(CLAVE) ?? ""; } catch { return ""; } };
const guardar = (id: string) => { try { localStorage.setItem(CLAVE, id); } catch { /* opcional */ } };

export const camaraDisponible = () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;

export function Camara({ alCapturar, alCerrar, frontal = false }: { alCapturar: (archivo: File) => void; alCerrar: () => void; frontal?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const flujo = useRef<MediaStream | null>(null);
  const [camaras, setCamaras] = useState<MediaDeviceInfo[]>([]);
  const [actual, setActual] = useState(leer);
  const [error, setError] = useState("");
  const [lista, setLista] = useState(false);
  const [destello, setDestello] = useState(false);

  async function abrir(deviceId: string) {
    flujo.current?.getTracks().forEach((t) => t.stop());
    setLista(false); setError("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: deviceId ? { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } } : { facingMode: { ideal: frontal ? "user" : "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      flujo.current = s;
      if (video.current) { video.current.srcObject = s; await video.current.play().catch(() => undefined); }
      const id = s.getVideoTracks()[0]?.getSettings().deviceId ?? "";
      if (id) { setActual(id); guardar(id); }
      // con el permiso ya concedido, el navegador entrega los nombres de las cámaras
      setCamaras((await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput"));
      setLista(true);
    } catch (e) {
      const n = (e as DOMException).name;
      if (deviceId && (n === "OverconstrainedError" || n === "NotFoundError")) { guardar(""); return abrir(""); } // la cámara recordada ya no está
      setError(n === "NotAllowedError" ? "El permiso de la cámara está bloqueado. Actívalo en el candado de la barra de direcciones (Configuración del sitio → Cámara → Permitir) y vuelve a intentar."
        : n === "NotFoundError" ? "No se encontró ninguna cámara en este equipo."
        : n === "NotReadableError" ? "La cámara está siendo usada por otra aplicación (Zoom, Teams…). Ciérrala y vuelve a intentar."
        : "No se pudo abrir la cámara.");
    }
  }
  useEffect(() => { abrir(actual); return () => flujo.current?.getTracks().forEach((t) => t.stop()); }, []);
  useEffect(() => { const f = (e: KeyboardEvent) => { if (e.key === "Escape") alCerrar(); }; addEventListener("keydown", f); return () => removeEventListener("keydown", f); }, []);

  function capturar() {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d")!.drawImage(v, 0, 0);
    setDestello(true); setTimeout(() => setDestello(false), 150);
    c.toBlob((b) => { if (b) alCapturar(new File([b], `foto-${Date.now()}.jpg`, { type: "image/jpeg" })); }, "image/jpeg", 0.92);
  }

  return (
    <div className="visor camara" role="dialog" aria-modal="true" aria-label="Cámara">
      <div className={`camara-marco${destello ? " destello" : ""}`}>
        <video ref={video} playsInline muted aria-label="Vista de la cámara" />
        {!lista && !error && <p className="camara-msg">Abriendo la cámara… si el navegador lo pregunta, elige «Permitir».</p>}
        {error && <p className="camara-msg error">{error}</p>}
      </div>
      <div className="fila visor-barra">
        {camaras.length > 1 && (
          <select className="entrada" style={{ width: "auto", maxWidth: 240 }} value={actual} onChange={(e) => abrir(e.target.value)} aria-label="Cámara">
            {camaras.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Cámara ${i + 1}`}</option>)}
          </select>
        )}
        <button className="btn pri disparador" onClick={capturar} disabled={!lista} aria-label="Tomar la foto">📷 Tomar</button>
        {error && <button className="btn" onClick={() => abrir(actual)}>Reintentar</button>}
        <button className="btn" onClick={alCerrar}>Listo</button>
      </div>
    </div>
  );
}
